import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { AuthContext } from '../../components/AuthContext';
import { FiEdit2, FiSearch, FiPlus, FiX, FiTrash2 } from 'react-icons/fi';
import PANVerificationModal from '../../components/PANVerificationModal';

export default function ReferenceManagement() {
  const { user } = useContext(AuthContext);

  const [references, setReferences] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Pagination and search
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalReferences, setTotalReferences] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Station dropdown
  const [stations, setStations] = useState([]);

  // Classification and Type options
  const classificationOptions = [
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'E', label: 'E' }
  ];

  const typeOptions = [
    { value: 'Interior', label: 'Interior' },
    { value: 'Designer', label: 'Designer' },
    { value: 'Architect', label: 'Architect' },
    { value: 'Builder', label: 'Builder' },
    { value: 'Contractor', label: 'Contractor' },
    { value: 'Mason', label: 'Mason' },
    { value: 'Plumber', label: 'Plumber' }
  ];

  // Form fields
  const [form, setForm] = useState({
    name: '',
    phoneNumber: '',
    secondaryPhoneNumber: '',
    tertiaryPhoneNumber: '',
    classification: 'A',
    type: 'Interior',
    station: '',
    panNumber: '',
    emailAddress: '',
    ifscCode: '',
    accountName: '',
    accountNumber: '',
    createdBy: user?.user?._id
  });

  // PAN verification states
  const [showPANVerification, setShowPANVerification] = useState(false);
  const [isPanReadOnly, setIsPanReadOnly] = useState(false);

  // IFSC verification state
  const [ifscValidated, setIfscValidated] = useState(false);
  const [ifscBankName, setIfscBankName] = useState('');
  const [ifscLoading, setIfscLoading] = useState(false);

  // Inline form errors
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchReferences();
  }, [page, limit, searchTerm]);

  useEffect(() => {
    fetchStations();
  }, []);

  // Dark mode sync with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Fetch available stations for dropdown
  const fetchStations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`);
      const stationOptions = response.data.map((st) => ({
        value: st._id,
        label: st.name
      }));
      setStations(stationOptions);
    } catch (err) {
      Swal.fire('Error', 'Failed to fetch stations', 'error');
    }
  };

  // Fetch References with pagination + search
  const fetchReferences = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/references`, {
        params: {
          limit,
          page,
          search: searchTerm,
          userCompanyId: user.company
        }
      });
      setReferences(response.data.references);
      setTotalReferences(response.data.total);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch references', 'error');
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!form.name || !form.station || !form.classification || !form.type) {
      Swal.fire('Error', 'Please fill all the required fields', 'error');
      return false;
    }
    const phoneFields = ['phoneNumber', 'secondaryPhoneNumber', 'tertiaryPhoneNumber'];
    for (const field of phoneFields) {
      if (form[field] && !/^\d{10}$/.test(form[field])) {
        Swal.fire('Error', `${field === 'phoneNumber' ? 'Phone number' : field === 'secondaryPhoneNumber' ? 'Secondary phone number' : 'Tertiary phone number'} should be a 10-digit number containing only digits`, 'error');
        return false;
      }
    }
    // Ensure all provided phone numbers are distinct
    const providedPhones = phoneFields.filter((field) => form[field]);
    if (new Set(providedPhones.map((field) => form[field])).size !== providedPhones.length) {
      Swal.fire('Error', 'Primary, secondary and tertiary phone numbers must be different', 'error');
      return false;
    }
    // Validate PAN number format if provided
    if (form.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNumber.toUpperCase())) {
      Swal.fire('Error', 'Please enter a valid PAN number (e.g., ABCDE1234F)', 'error');
      return false;
    }
    // Validate email format if provided
    if (form.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailAddress)) {
      Swal.fire('Error', 'Please enter a valid email address', 'error');
      return false;
    }
    // Validate IFSC code format if provided
    if (form.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifscCode.toUpperCase())) {
      Swal.fire('Error', 'Please enter a valid IFSC code (e.g., SBIN0000001)', 'error');
      return false;
    }
    // Validate account name if provided - letters, spaces, dots, hyphens only
    if (form.accountName && !/^[A-Za-z\s.\-]{2,100}$/.test(form.accountName.trim())) {
      Swal.fire('Error', 'Account name should contain only letters, spaces, dots or hyphens (2-100 characters)', 'error');
      return false;
    }
    // Validate account number if provided - digits only, 6-20 characters
    if (form.accountNumber && !/^\d{6,20}$/.test(form.accountNumber.trim())) {
      Swal.fire('Error', 'Account number should be 6-20 digits', 'error');
      return false;
    }
    setFormErrors(errors);
    return true;
  };

  // Create / Update submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/references/${editingId}`,
          form
        );
        Swal.fire('Success', 'Reference updated successfully', 'success');
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/references`, form);
        Swal.fire('Success', 'Reference added successfully', 'success');
      }
      fetchReferences();
      resetForm();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error submitting reference:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit reference';
      Swal.fire('Error', errorMessage, 'error');
    }
  };

  // Reset form to default state
  const resetForm = () => {
    setForm({
      name: '',
      phoneNumber: '',
      secondaryPhoneNumber: '',
      tertiaryPhoneNumber: '',
      classification: 'A',
      type: 'Interior',
      station: '',
      panNumber: '',
      emailAddress: '',
      ifscCode: '',
      accountName: '',
      accountNumber: '',
      createdBy: user?.user?._id
    });
    setIsEditing(false);
    setEditingId(null);
    setIsPanReadOnly(false);
    setIfscValidated(false);
    setIfscBankName('');
    setFormErrors({});
  };

  // Input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle phone number inputs - only allow digits
    if (['phoneNumber', 'secondaryPhoneNumber', 'tertiaryPhoneNumber'].includes(name)) {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setForm({ ...form, [name]: numericValue });
      }
      return;
    }

    // Handle account number - only digits, max 20
    if (name === 'accountNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 20) {
        setForm({ ...form, accountNumber: digitsOnly });
      }
      return;
    }

    // Handle account name - only letters, spaces, dots, hyphens
    if (name === 'accountName') {
      const cleaned = value.replace(/[^A-Za-z\s.\-]/g, '');
      setForm({ ...form, accountName: cleaned });
      return;
    }
    
    // Convert reference name, PAN, IFSC and account name to uppercase
    const upperCaseFields = ['name', 'panNumber', 'ifscCode', 'accountName'];
    const processedValue = upperCaseFields.includes(name) ? value.toUpperCase() : value;
    setForm({ ...form, [name]: processedValue });
  };

  // Station select
  const handleStationChange = (selectedOption) => {
    setForm({ ...form, station: selectedOption.value });
  };

  // Classification select
  const handleClassificationChange = (selectedOption) => {
    setForm({ ...form, classification: selectedOption.value });
  };

  // Type select
  const handleTypeChange = (selectedOption) => {
    setForm({ ...form, type: selectedOption.value });
  };

  // PAN verification handler (Reference has no GST field - linked GSTINs shown as info only)
  const handlePANVerificationData = ({ panNo, verificationData }) => {
    setForm(prev => ({ ...prev, panNumber: panNo }));
    setIsPanReadOnly(true);

    const gstinList = verificationData.gstinResList || [];

    Swal.fire({
      icon: 'success',
      title: 'PAN Verified Successfully!',
      html: `
        <div class="text-left">
          <p class="mb-2"><strong>PAN:</strong> ${panNo}</p>
          ${gstinList.length > 0
            ? `<p class="mb-2 font-semibold">This PAN has ${gstinList.length} linked GSTIN(s):</p>
               <ul class="text-sm mb-3 space-y-1">${gstinList.map(item => `<li>${item.gstin} (${item.authStatus || 'N/A'} - State: ${item.stateCd || 'N/A'})</li>`).join('')}</ul>`
            : `<p class="text-sm text-yellow-600 mb-3">⚠️ No linked GSTINs found for this PAN.</p>`}
          <p class="text-sm text-blue-600">ℹ️ PAN has been verified against the government portal.</p>
        </div>
      `,
      confirmButtonText: 'OK',
      confirmButtonColor: '#9333EA'
    });
  };

  const handleClearPAN = () => {
    setForm(prev => ({ ...prev, panNumber: '' }));
    setIsPanReadOnly(false);
  };

  // IFSC verification handler
  const handleVerifyIFSC = async () => {
    const ifsc = form.ifscCode?.trim().toUpperCase();
    if (!ifsc) {
      Swal.fire('Error', 'Please enter an IFSC code first', 'error');
      return;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
      Swal.fire('Error', 'Please enter a valid IFSC code format (e.g., SBIN0000001)', 'error');
      return;
    }
    setIfscLoading(true);
    try {
      const response = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
      if (response.ok) {
        const data = await response.json();
        setIfscValidated(true);
        setIfscBankName(data.BANK || '');
        Swal.fire({
          icon: 'success',
          title: 'IFSC Verified!',
          html: `<div class="text-left"><p><strong>Bank:</strong> ${data.BANK || 'N/A'}</p><p><strong>Branch:</strong> ${data.BRANCH || 'N/A'}</p><p><strong>City:</strong> ${data.CITY || 'N/A'}</p><p><strong>State:</strong> ${data.STATE || 'N/A'}</p></div>`,
          confirmButtonColor: '#9333EA'
        });
      } else {
        setIfscValidated(false);
        setIfscBankName('');
        Swal.fire('Error', 'Invalid IFSC code. Please check and try again.', 'error');
      }
    } catch (err) {
      setIfscValidated(false);
      setIfscBankName('');
      Swal.fire('Error', 'Failed to verify IFSC code. Please try again.', 'error');
    } finally {
      setIfscLoading(false);
    }
  };

  const handleClearIFSC = () => {
    setForm(prev => ({ ...prev, ifscCode: '' }));
    setIfscValidated(false);
    setIfscBankName('');
  };

  // Pagination
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };


  const handleDeleteReference = async (reference) => {
    const result = await Swal.fire({
      title: 'Delete reference?',
      text: `Delete ${reference.name}? This is allowed only if it is not used anywhere.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      setDeletingId(reference._id);
      const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/references/${reference._id}`, {
        params: { userCompanyId: user?.company }
      });

      Swal.fire('Deleted', response?.data?.message || 'Reference deleted successfully', 'success');

      if (references.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchReferences();
      }
    } catch (error) {
      const usageList = error?.response?.data?.usage;
      const usageText = Array.isArray(usageList) && usageList.length > 0
        ? usageList.map((u) => `${u.count} ${u.label}`).join(', ')
        : null;

      Swal.fire(
        'Cannot Delete',
        usageText
          ? `Reference is in use: ${usageText}`
          : (error?.response?.data?.message || 'Failed to delete reference'),
        'error',
      );
    } finally {
      setDeletingId(null);
    }
  };
  const totalPages = Math.ceil(totalReferences / limit);

  // Enhanced pagination helpers
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
      range.push(i);
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (page + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  // Helper function for Select styles
  const getSelectStyles = (isDark) => ({
    control: (base, state) => ({
      ...base,
      border: state.isFocused 
        ? `2px solid ${isDark ? '#9333EA' : '#9333EA'}` 
        : `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`,
      borderRadius: '12px',
      padding: '4px',
      backgroundColor: isDark ? '#374151' : 'white',
      boxShadow: state.isFocused ? `0 0 0 1px ${isDark ? '#9333EA' : '#9333EA'}` : 'none',
      '&:hover': {
        borderColor: isDark ? '#6B7280' : '#D1D5DB'
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : 'white',
      borderRadius: '12px',
      border: `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#9333EA' 
        : state.isFocused 
        ? (isDark ? '#4B5563' : '#F3E8FF')
        : (isDark ? '#374151' : 'white'),
      color: state.isSelected ? 'white' : (isDark ? '#F3F4F6' : '#374151'),
      '&:hover': {
        backgroundColor: state.isSelected ? '#9333EA' : (isDark ? '#4B5563' : '#F3E8FF')
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#374151'
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#374151'
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? '#9CA3AF' : '#6B7280'
    })
  });

  return (
    <div className={`min-h-screen p-3 sm:p-6 ${
      darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-purple-50'
    }`}>
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className={`rounded-2xl shadow-xl p-6 mb-8 border-l-4 border-purple-500 ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                Reference Management
              </h1>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Manage and organize your reference partners
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative group">
                <FiSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors ${
                  darkMode ? 'text-gray-500 group-focus-within:text-purple-400' : 'text-gray-400 group-focus-within:text-purple-500'
                }`} />
                <input
                  type="text"
                  placeholder="Search references..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 transition-all w-full sm:w-72 shadow-sm hover:shadow-md ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-purple-500' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-purple-500'
                  }`}
                />
              </div>
              <button
                onClick={() => setIsFormOpen(true)}
                className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
              >
                <FiPlus className="text-lg" /> Add New Reference
              </button>
            </div>
          </div>
        </div>

        {/* Form Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
            <div className={`rounded-2xl shadow-2xl w-full max-w-5xl h-full max-h-[98vh] flex flex-col transform animate-in slide-in-from-bottom duration-300 ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className={`flex justify-between items-center px-6 py-4 border-b flex-shrink-0 ${
                darkMode ? 'border-gray-700' : 'border-gray-100'
              }`}>
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  {isEditing ? 'Edit Reference' : 'Add New Reference'}
                </h2>
                <button onClick={() => { setIsFormOpen(false); resetForm(); }}
                  className={`p-2 rounded-full transition-colors ${
                    darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}>
                  <FiX size={24} />
                </button>
              </div>

              <div className="px-6 py-5 space-y-6 flex-1 min-h-0 overflow-y-auto">
                {/* ── Basic Details ── */}
                <div>
                  <h3 className={`text-sm font-bold uppercase tracking-wider mb-4 pb-2 border-b ${darkMode ? 'text-purple-400 border-gray-700' : 'text-purple-600 border-gray-200'}`}>
                    Basic Details
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name *</label>
                      <input name="name" type="text" value={form.name} onChange={handleInputChange} placeholder="Enter reference name"
                        className={`w-full border rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-purple-500 transition-all uppercase ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                        }`} style={{ textTransform: 'uppercase' }} />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Phone Number *</label>
                        <input name="phoneNumber" type="tel" value={form.phoneNumber} onChange={handleInputChange} placeholder="10-digit phone" maxLength="10"
                          className={`w-full border rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-purple-500 transition-all ${
                            darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                          }`} />
                      </div>
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Secondary Phone</label>
                        <input name="secondaryPhoneNumber" type="tel" value={form.secondaryPhoneNumber} onChange={handleInputChange} placeholder="Optional" maxLength="10"
                          className={`w-full border rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-purple-500 transition-all ${
                            darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                          }`} />
                      </div>
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Third Phone</label>
                        <input name="tertiaryPhoneNumber" type="tel" value={form.tertiaryPhoneNumber} onChange={handleInputChange} placeholder="Optional" maxLength="10"
                          className={`w-full border rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-purple-500 transition-all ${
                            darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                          }`} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Classification *</label>
                        <Select options={classificationOptions} value={classificationOptions.find((opt) => opt.value === form.classification)}
                          onChange={handleClassificationChange} placeholder="Select classification"
                          className="react-select-container" classNamePrefix="react-select" styles={getSelectStyles(darkMode)} />
                      </div>
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Type *</label>
                        <Select options={typeOptions} value={typeOptions.find((opt) => opt.value === form.type)}
                          onChange={handleTypeChange} placeholder="Select type"
                          className="react-select-container" classNamePrefix="react-select" styles={getSelectStyles(darkMode)} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Station *</label>
                        <Select options={stations} value={stations.find((opt) => opt.value === form.station)}
                          onChange={handleStationChange} placeholder="Select station"
                          className="react-select-container" classNamePrefix="react-select" styles={getSelectStyles(darkMode)} />
                      </div>
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email Address</label>
                        <input name="emailAddress" type="email" value={form.emailAddress} onChange={handleInputChange} placeholder="Enter email address"
                          className={`w-full border rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-purple-500 transition-all ${
                            darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                          }`} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        PAN Number {isPanReadOnly && <span className="text-xs text-green-500 ml-1">(Verified)</span>}
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <input name="panNumber" type="text" value={form.panNumber} onChange={handleInputChange}
                            placeholder="e.g., ABCDE1234F" maxLength="10" disabled={isPanReadOnly}
                            className={`w-full border rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-purple-500 transition-all uppercase ${
                              isPanReadOnly ? 'opacity-75 cursor-not-allowed' : ''
                            } ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'}`}
                            style={{ textTransform: 'uppercase' }} />
                          {form.panNumber && (
                            <button type="button" onClick={handleClearPAN}
                              className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors ${
                                darkMode ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600' : 'text-gray-400 hover:text-red-500 hover:bg-gray-100'
                              }`} title="Clear PAN">✕</button>
                          )}
                        </div>
                        <button type="button" onClick={() => setShowPANVerification(true)} disabled={isPanReadOnly}
                          className={`px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                            isPanReadOnly
                              ? darkMode ? 'bg-green-900/30 text-green-400 cursor-not-allowed' : 'bg-green-50 text-green-600 cursor-not-allowed'
                              : darkMode ? 'bg-purple-900/30 text-purple-400 hover:bg-purple-800/50' : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                          }`}>
                          {isPanReadOnly ? 'Verified' : 'Verify'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Bank Details ── */}
                <div>
                  <h3 className={`text-sm font-bold uppercase tracking-wider mb-4 pb-2 border-b ${darkMode ? 'text-purple-400 border-gray-700' : 'text-purple-600 border-gray-200'}`}>
                    Bank Details <span className={`font-normal normal-case ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>(Optional)</span>
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        IFSC Code {ifscValidated && ifscBankName && <span className="text-xs text-green-500 ml-1">(Verified - {ifscBankName})</span>}
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <input name="ifscCode" type="text" value={form.ifscCode}
                            onChange={(e) => {
                              const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                              if (val.length <= 11) {
                                setForm({ ...form, ifscCode: val });
                                if (val.length !== 11) { setIfscValidated(false); setIfscBankName(''); }
                                if (val.length === 11 && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(val)) {
                                  setIfscLoading(true);
                                  fetch(`https://ifsc.razorpay.com/${val}`)
                                    .then(r => r.ok ? r.json() : Promise.reject())
                                    .then(data => { setIfscValidated(true); setIfscBankName(data.BANK || ''); })
                                    .catch(() => { setIfscValidated(false); setIfscBankName(''); })
                                    .finally(() => setIfscLoading(false));
                                }
                              }
                            }}
                            placeholder="e.g., SBIN0000001" maxLength="11"
                            className={`w-full border rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-purple-500 transition-all uppercase ${
                              form.ifscCode && form.ifscCode.length === 11 && !ifscValidated && !ifscLoading ? 'border-red-500'
                                : ifscValidated ? 'border-green-500' : ''
                            } ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'}`}
                            style={{ textTransform: 'uppercase' }} />
                          {form.ifscCode && (
                            <button type="button" onClick={handleClearIFSC}
                              className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors ${
                                darkMode ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600' : 'text-gray-400 hover:text-red-500 hover:bg-gray-100'
                              }`} title="Clear">✕</button>
                          )}
                        </div>
                        <button type="button" onClick={handleVerifyIFSC} disabled={ifscLoading || ifscValidated}
                          className={`px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                            ifscValidated ? darkMode ? 'bg-green-900/30 text-green-400 cursor-not-allowed' : 'bg-green-50 text-green-600 cursor-not-allowed'
                              : ifscLoading ? 'opacity-60 cursor-wait'
                              : darkMode ? 'bg-purple-900/30 text-purple-400 hover:bg-purple-800/50' : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                          }`}>
                          {ifscValidated ? 'Verified' : ifscLoading ? 'Checking...' : 'Verify'}
                        </button>
                      </div>
                      {form.ifscCode && form.ifscCode.length === 11 && !ifscValidated && !ifscLoading && (
                        <p className="text-xs text-red-500 mt-1">Invalid IFSC code. Please verify.</p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Account Name</label>
                        <input name="accountName" type="text" value={form.accountName} onChange={handleInputChange}
                          placeholder="Account holder name"
                          className={`w-full border rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-purple-500 transition-all uppercase ${
                            form.accountName && !/^[A-Za-z\s.\-]{2,100}$/.test(form.accountName) ? 'border-red-500' : ''
                          } ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'}`}
                          style={{ textTransform: 'uppercase' }} />
                        {form.accountName && !/^[A-Za-z\s.\-]{2,100}$/.test(form.accountName) && (
                          <p className="text-xs text-red-500">Letters, spaces, dots or hyphens only (min 2)</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Account Number</label>
                        <input name="accountNumber" type="text" value={form.accountNumber} onChange={handleInputChange}
                          placeholder="6-20 digits only"
                          className={`w-full border rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-purple-500 transition-all ${
                            form.accountNumber && !/^\d{6,20}$/.test(form.accountNumber) ? 'border-red-500' : ''
                          } ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'}`} />
                        {form.accountNumber && !/^\d{6,20}$/.test(form.accountNumber) && (
                          <p className="text-xs text-red-500">Must be 6-20 digits</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fixed Footer */}
              <div className={`flex gap-3 px-6 py-4 border-t flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <button onClick={() => { setIsFormOpen(false); resetForm(); }}
                  className={`flex-1 px-4 py-3 rounded-xl transition-colors font-medium ${
                    darkMode ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}>
                  Cancel
                </button>
                <button onClick={handleSubmit}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-4 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200">
                  {isEditing ? 'Update' : 'Add'} Reference
                </button>
              </div>
            </div>
          </div>
        )}

        {/* References Table */}
        <div className={`rounded-2xl shadow-xl overflow-hidden border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
        }`}>
          <div className={`px-6 py-4 border-b ${
            darkMode ? 'bg-gray-750 border-gray-700' : 'bg-gradient-to-r from-gray-50 to-purple-50 border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold flex items-center gap-2 ${
              darkMode ? 'text-gray-100' : 'text-gray-800'
            }`}>
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              References List
              <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ({totalReferences} total)
              </span>
            </h3>
          </div>

          {/* Desktop Table View - Hidden on Mobile */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={darkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-3 py-4 text-left text-xs font-bold uppercase tracking-wider w-16 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>ID</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Name</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Phone</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>PAN</th>
                  <th className={`px-3 py-4 text-left text-xs font-bold uppercase tracking-wider w-20 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Class</th>
                  <th className={`px-3 py-4 text-left text-xs font-bold uppercase tracking-wider w-24 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Type</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Station</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>IFSC</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Account Name</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Account No</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider w-20 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${
                darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-100'
              }`}>
                {references.map((reference, index) => (
                  <tr key={reference._id} className={`transition-colors group ${
                    darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                  }`}>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span className={`text-xs font-mono px-2 py-1 rounded-md ${
                        darkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-600 bg-gray-100'
                      }`}>
                        {reference.id}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                        {reference.name}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {reference.phoneNumber}
                      </span>
                      {(reference.secondaryPhoneNumber || reference.tertiaryPhoneNumber) && (
                        <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {[reference.secondaryPhoneNumber, reference.tertiaryPhoneNumber].filter(Boolean).join('  •  ')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`text-xs font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {reference.panNumber || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        reference.classification === 'A' ? 'bg-red-100 text-red-800 ring-1 ring-red-200' :
                        reference.classification === 'B' ? 'bg-orange-100 text-orange-800 ring-1 ring-orange-200' :
                        reference.classification === 'C' ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200' :
                        reference.classification === 'D' ? 'bg-green-100 text-green-800 ring-1 ring-green-200' :
                        'bg-blue-100 text-blue-800 ring-1 ring-blue-200'
                      }`}>
                        {reference.classification}
                      </span>
                    </td>
                    <td className="px-3 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium truncate max-w-20 ${
                        darkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {reference.type}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium truncate max-w-24 ${
                        darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {reference.station?.name || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-xs font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {reference.ifscCode || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {reference.accountName || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-xs font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {reference.accountNumber || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setIsEditing(true);
                            setEditingId(reference._id);
                            setForm({
                              name: reference.name,
                              phoneNumber: reference.phoneNumber,
                              secondaryPhoneNumber: reference.secondaryPhoneNumber || '',
                              tertiaryPhoneNumber: reference.tertiaryPhoneNumber || '',
                              classification: reference.classification,
                              type: reference.type,
                              station: reference.station?._id,
                              panNumber: reference.panNumber || '',
                              emailAddress: reference.emailAddress || '',
                              ifscCode: reference.ifscCode || '',
                              accountName: reference.accountName || '',
                              accountNumber: reference.accountNumber || '',
                              createdBy: user?.user?._id
                            });
                            setIfscValidated(!!reference.ifscCode);
                            setIfscBankName('');
                            setIsFormOpen(true);
                          }}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all group-hover:shadow-md ${
                            darkMode 
                              ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-900' 
                              : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50'
                          }`}
                        >
                          <FiEdit2 size={14} />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDeleteReference(reference)}
                          disabled={deletingId === reference._id}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all ${
                            darkMode
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-900'
                              : 'text-red-600 hover:text-red-800 hover:bg-red-50'
                          } disabled:opacity-60 disabled:cursor-not-allowed`}
                        >
                          <FiTrash2 size={14} />
                          <span>{deletingId === reference._id ? 'Deleting...' : 'Delete'}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {references.length === 0 && (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          <FiSearch className={`text-2xl ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                        <p className={`text-lg font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          No references found
                        </p>
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Try adjusting your search terms
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Hidden on Desktop */}
          <div className="md:hidden">
            {references.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {references.map((reference, index) => (
                  <div 
                    key={reference._id} 
                    className={`p-4 transition-colors ${
                      darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                            darkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-600 bg-gray-100'
                          }`}>
                            {reference.id}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                            reference.classification === 'A' ? 'bg-red-100 text-red-800' :
                            reference.classification === 'B' ? 'bg-orange-100 text-orange-800' :
                            reference.classification === 'C' ? 'bg-yellow-100 text-yellow-800' :
                            reference.classification === 'D' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {reference.classification}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            darkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {reference.type}
                          </span>
                        </div>
                        <h3 className={`text-base font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {reference.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setIsEditing(true);
                            setEditingId(reference._id);
                            setForm({
                              name: reference.name,
                              phoneNumber: reference.phoneNumber,
                              secondaryPhoneNumber: reference.secondaryPhoneNumber || '',
                              tertiaryPhoneNumber: reference.tertiaryPhoneNumber || '',
                              classification: reference.classification,
                              type: reference.type,
                              station: reference.station?._id,
                              panNumber: reference.panNumber || '',
                              emailAddress: reference.emailAddress || '',
                              ifscCode: reference.ifscCode || '',
                              accountName: reference.accountName || '',
                              accountNumber: reference.accountNumber || '',
                              createdBy: user?.user?._id
                            });
                            setIfscValidated(!!reference.ifscCode);
                            setIfscBankName('');
                            setIsFormOpen(true);
                          }}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all shadow-sm ${
                            darkMode 
                              ? 'text-purple-400 bg-purple-900 hover:bg-purple-800' 
                              : 'text-purple-600 bg-purple-50 hover:bg-purple-100'
                          }`}
                        >
                          <FiEdit2 size={14} />
                          <span className="text-sm font-medium">Edit</span>
                        </button>
                        <button
                          onClick={() => handleDeleteReference(reference)}
                          disabled={deletingId === reference._id}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all shadow-sm ${
                            darkMode
                              ? 'text-red-400 bg-red-900 hover:bg-red-800'
                              : 'text-red-600 bg-red-50 hover:bg-red-100'
                          } disabled:opacity-60 disabled:cursor-not-allowed`}
                        >
                          <FiTrash2 size={14} />
                          <span className="text-sm font-medium">{deletingId === reference._id ? 'Deleting...' : 'Delete'}</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <svg className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {reference.phoneNumber}
                        </span>
                        {(reference.secondaryPhoneNumber || reference.tertiaryPhoneNumber) && (
                          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {' '}{[reference.secondaryPhoneNumber, reference.tertiaryPhoneNumber].filter(Boolean).join('  •  ')}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <svg className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {reference.station?.name || 'N/A'}
                        </span>
                      </div>
                      {(reference.ifscCode || reference.accountName || reference.accountNumber) && (
                        <div className={`flex items-center gap-2 pt-1 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                          <svg className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {[reference.ifscCode, reference.accountName, reference.accountNumber].filter(Boolean).join(' • ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-12 text-center">
                <div className="flex flex-col items-center justify-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <FiSearch className={`text-2xl ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <p className={`text-lg font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No references found
                  </p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Try adjusting your search terms
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`rounded-2xl shadow-lg border mt-6 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
          }`}>
            <div className={`px-4 sm:px-6 py-3 sm:py-4 border-t ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="hidden sm:inline">Showing </span>
                    <span className="font-medium">{((page - 1) * limit) + 1}</span>
                    <span className="hidden sm:inline"> to </span>
                    <span className="sm:hidden">-</span>
                    <span className="font-medium">{Math.min(page * limit, totalReferences)}</span>
                    <span className="hidden sm:inline"> of </span>
                    <span className="sm:hidden">/</span>
                    <span className="font-medium">{totalReferences}</span>
                    <span className="hidden sm:inline"> results</span>
                  </p>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(parseInt(e.target.value));
                      setPage(1);
                    }}
                    className={`px-2 sm:px-3 py-1 border rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-purple-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                
                <nav className="flex items-center justify-center sm:justify-start space-x-1">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className={`relative inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium border rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      darkMode 
                        ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                        : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </button>
                  
                  <div className="hidden sm:flex items-center space-x-1">
                    {getPageNumbers().map((pageNum, index) => (
                      pageNum === '...' ? (
                        <span key={index} className={`px-3 py-1 text-sm ${
                          darkMode ? 'text-gray-500' : 'text-gray-500'
                        }`}>...</span>
                      ) : (
                        <button
                          key={index}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-3 py-1 text-sm font-medium border rounded-md transition-all ${
                            pageNum === page
                              ? darkMode 
                                ? 'z-10 bg-purple-500/20 border-purple-500 text-purple-400' 
                                : 'z-10 bg-purple-50 border-purple-500 text-purple-600'
                              : darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' 
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    ))}
                  </div>
                  
                  {/* Mobile: Show only current page */}
                  <div className="sm:hidden flex items-center">
                    <span className={`px-2 py-1 text-xs font-medium ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {page} / {totalPages}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className={`relative inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium border rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      darkMode 
                        ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                        : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PAN Verification Modal */}
      <PANVerificationModal
        isOpen={showPANVerification}
        onClose={() => setShowPANVerification(false)}
        onDataReceived={handlePANVerificationData}
        initialPAN={form.panNumber}
      />
    </div>
  );
}